import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { QrCode, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SearchPage() {
  useDocumentTitle("Ricerca");
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const scan = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    try {
      const { data } = await api.get(`/interventions/by-code/${code.trim()}`);
      nav(`/interventi/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Codice non trovato");
    }
  };

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/interventions", { params: { q: q.trim() } });
      setResults(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// RICERCA</div>
        <h1 className="text-4xl font-display font-bold tracking-tight">Cerca o scansiona</h1>
        <p className="text-muted-foreground mt-2">Scansiona il QR sull&apos;etichetta o cerca per nome, telefono o modello.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* QR Scan */}
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <QrCode size={20} weight="duotone" className="text-primary" />
            <h2 className="font-display font-bold tracking-tight text-lg">Scansione QR</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Punta il lettore barcode sul codice, oppure inserisci a mano.
          </p>
          <form onSubmit={scan} className="flex gap-2">
            <Input
              autoFocus
              placeholder="INT-20260201-0001"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="scan-code-input"
              className="font-mono uppercase"
            />
            <Button type="submit" data-testid="scan-submit-btn" className="rounded-full gap-2 shrink-0">
              Apri <ArrowRight size={16} weight="bold" />
            </Button>
          </form>
        </Card>

        {/* Text search */}
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <MagnifyingGlass size={20} weight="duotone" className="text-primary" />
            <h2 className="font-display font-bold tracking-tight text-lg">Ricerca testuale</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Nome, cognome, telefono, marca, modello, seriale…
          </p>
          <form onSubmit={search} className="flex gap-2">
            <Input
              placeholder="Rossi Mario, 3331234567…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="text-search-input"
            />
            <Button type="submit" variant="outline" data-testid="text-search-submit-btn" className="rounded-full shrink-0">
              Cerca
            </Button>
          </form>
        </Card>
      </div>

      {loading && <div className="text-center text-muted-foreground py-8">Ricerca in corso…</div>}

      {results.length > 0 && (
        <Card className="border-border/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border/60 bg-secondary/50">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              {results.length} risultati
            </div>
          </div>
          <div className="divide-y divide-border/50">
            {results.map((r) => (
              <button
                key={r.id}
                onClick={() => nav(`/interventi/${r.id}`)}
                data-testid={`result-${r.code}`}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/40 text-left"
                style={{ transition: "background-color 0.15s ease" }}
              >
                <div>
                  <div className="font-mono text-xs text-primary">{r.code}</div>
                  <div className="font-medium mt-0.5">{r.customer_surname} {r.customer_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.product_type} {r.product_brand} {r.product_model} — {r.work_description.slice(0, 50)}
                  </div>
                </div>
                <ArrowRight size={16} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
